import { apiClient, apiFetch, apiPatch, apiPost } from '@/utils/fetchWrapper';
import { recordingFileRequestPath } from '@/utils/callHistoryUtils';
import {
  buildCallHistoryQueryString,
  buildPaginatedPath,
} from '@/utils/queryParams';
import {
  ActiveCall,
  Call,
  PaginatedCallsResponse,
  CallHistoryFilters,
  CallRecordingsResponse,
  CallTranscriptsResponse,
} from '@/types/calls';

export const callsService = {
  async getNominalCallHistory(
    filters: CallHistoryFilters = {}
  ): Promise<PaginatedCallsResponse> {
    const url = buildPaginatedPath(
      '/nominal_call/',
      buildCallHistoryQueryString(filters)
    );
    return apiFetch<PaginatedCallsResponse>(url);
  },

  async getCallRecordings(
    callUuid: string,
    isUserCall: boolean = false
  ): Promise<CallRecordingsResponse> {
    const endpoint = isUserCall
      ? `/user_call/${callUuid}/recordings/`
      : `/nominal_call/${callUuid}/recordings/`;
    return apiFetch<CallRecordingsResponse>(endpoint);
  },

  async getCallTranscripts(
    callUuid: string,
    finalOnly: boolean = true,
    isUserCall: boolean = false
  ): Promise<CallTranscriptsResponse> {
    const params = new URLSearchParams();
    params.append('final_only', finalOnly.toString());
    const endpoint = isUserCall
      ? `/user_call/${callUuid}/transcripts/?${params.toString()}`
      : `/nominal_call/${callUuid}/transcripts/?${params.toString()}`;
    return apiFetch<CallTranscriptsResponse>(endpoint);
  },

  /** Loads recording bytes with JWT auth (plain audio/video src cannot send Bearer tokens). */
  async fetchRecordingFile(fileUrl: string): Promise<Blob> {
    const response = await apiClient.get<Blob>(
      recordingFileRequestPath(fileUrl),
      {
        responseType: 'blob',
      }
    );
    return response.data;
  },

  async getCurrentCalls(): Promise<ActiveCall[]> {
    const response = await apiFetch<{ rows: ActiveCall[] }>('/current-calls/');
    return response.rows;
  },

  async getActiveCallsCount(userId?: string): Promise<number> {
    const activeCalls = await this.getCurrentCalls();
    if (userId) {
      return activeCalls.filter(
        call => call.user?.id === userId || call.user?.id?.toString() === userId
      ).length;
    }
    return activeCalls.length;
  },

  async getCallChartData(): Promise<Record<string, unknown>> {
    return apiFetch<Record<string, unknown>>('/calls-chart/');
  },

  async getRecentCalls(): Promise<Call[]> {
    const callHistory = await this.getNominalCallHistory({
      page: 1,
      page_size: 10,
    });
    return callHistory.results;
  },

  async getNominalCallHistoryById(
    nominalId: string,
    page: number = 1,
    pageSize: number = 20
  ): Promise<PaginatedCallsResponse> {
    const url = buildPaginatedPath(
      '/nominal_call/',
      buildCallHistoryQueryString(
        { page, page_size: pageSize },
        { nominal: nominalId }
      )
    );
    return apiFetch<PaginatedCallsResponse>(url);
  },

  async getNominalCallHistoryWithFilters(
    nominalId: string,
    filters: CallHistoryFilters = {}
  ): Promise<PaginatedCallsResponse> {
    const url = buildPaginatedPath(
      '/nominal_call/',
      buildCallHistoryQueryString(filters, { nominal: nominalId })
    );
    return apiFetch<PaginatedCallsResponse>(url);
  },
  async terminateCall(
    callUuid: string,
    payload: { termination_reason?: string } = {}
  ): Promise<{ success: boolean; message: string }> {
    const response = await apiPost<{ success: boolean; message: string }>(
      `/calls/${callUuid}/terminate/`,
      payload
    );
    return response;
  },

  // FreeSWITCH rings this dashboard's SIP registration, so the audio plays here.
  async eavesdropCall(callUuid: string): Promise<{ success: boolean }> {
    return apiPost(`/calls/${callUuid}/eavesdrop/`);
  },

  async interveneCall(callUuid: string): Promise<{ success: boolean }> {
    return apiPost(`/calls/${callUuid}/intervene/`);
  },

  async updateCallNotes(
    callUuid: string,
    notes: string
  ): Promise<{ notes: string }> {
    return apiPatch<{ notes: string }>(`/calls/${callUuid}/notes/`, { notes });
  },

  async updateCallTerminationReason(
    callUuid: string,
    terminationReason: string
  ): Promise<{ termination_reason: string }> {
    return apiPatch<{ termination_reason: string }>(
      `/calls/${callUuid}/termination-reason/`,
      { termination_reason: terminationReason }
    );
  },
};
